/**
 * The referrers list (docs/ux/registry.md "Read-only lists"): every
 * referrer on the registry with its kind, the days it allows and how many
 * of its matters are open, each row opening the pipeline filtered to it.
 * Read only: referrers arrive from the seed and, later, from VitalSign.
 */

import { useState } from 'react';
import { Link } from 'react-router';
import type { ReferrerRecord } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { PageHeader } from '@/components/shell/PageHeader';
import { ReferrerDialog } from '../intake/ListDialogs';
import { useShellWidth } from '@/components/shell/context';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePipelineRows } from '@/features/pipeline/usePipelineData';
import { RegistryPill } from './RegistryPill';
import { useReferrers } from './useRegistry';

export const REFERRER_KIND_WORD: Record<ReferrerRecord['kind'], string> = { plaintiff: 'Plaintiff (invoice before release)', standard: 'Standard' };

/** Open: not yet closed (12) and not cancelled (15). */
const isOpen = (status: number): boolean => status !== 12 && status !== 15;

export function ReferrersPage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const referrers = useReferrers();
  const matters = usePipelineRows();
  const openBy = new Map<string, number>();
  for (const m of matters.data ?? []) if (isOpen(m.status)) openBy.set(m.referrer.id, (openBy.get(m.referrer.id) ?? 0) + 1);
  const rows = [...(referrers.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const [dialog, setDialog] = useState<{ open: boolean; existing: ReferrerRecord | null }>({ open: false, existing: null });
  const header = (
    <PageHeader
      title="Referrers"
      description="Who instructs Beam, the days each allows, and their open cases."
      tabs={<RegistryPill current="referrers" />}
      actions={<Button className="h-11 md:h-9" onClick={() => setDialog({ open: true, existing: null })}>New referrer</Button>}
    />
  );

  if (referrers.isError) return <div className="flex flex-col gap-3">{header}<ErrorPanel message="The referrers could not be loaded." onRetry={() => void referrers.refetch()} /></div>;
  if (referrers.isPending) return <div className="flex flex-col gap-3">{header}<Skeleton className="h-64 rounded-xl" aria-label="Loading referrers" /></div>;
  if (rows.length === 0) return <div className="flex flex-col gap-3">{header}<EmptyState title="No referrers on the registry yet." description="The seed creates the demo referrers; Beam's referrers arrive with VitalSign." /></div>;

  const open = (r: ReferrerRecord) => (matters.isPending ? null : openBy.get(r.id) ?? 0);
  const pipelineHref = (r: ReferrerRecord) => `/pipeline?referrer=${encodeURIComponent(r.id)}`;

  const dialogEl = <ReferrerDialog open={dialog.open} existing={dialog.existing} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />;

  return (
    <div className="flex flex-col gap-3">
      {header}
      {dialogEl}
      {phone ? (
        <ul className="flex flex-col gap-2" data-slot="referrer-cards">
          {rows.map((r) => (
            <li key={r.id} data-slot="referrer-card" data-id={r.id} className="flex flex-col gap-1 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm">
              <span className="font-medium">{r.name}</span>
              <span className="text-beam-text-muted">{REFERRER_KIND_WORD[r.kind]}</span>
              <span className="text-beam-text-muted"><span className="tabular">{r.slaDays}</span> days allowed, <span className="tabular">{open(r) ?? '...'}</span> open</span>
              <Link to={pipelineHref(r)} className={buttonVariants({ variant: 'outline', size: 'sm' }) + ' mt-1 self-start'} aria-label={`Open ${r.name} in the pipeline`}>Open in pipeline</Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-beam-line bg-beam-paper">
          <Table data-slot="referrer-table" className="text-[13px]">
            <caption className="sr-only">Referrers on the registry, by name</caption>
            <TableHeader className="bg-beam-cream-deep">
              <TableRow className="border-beam-line hover:bg-beam-cream-deep">
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Referrer</TableHead>
                <TableHead scope="col" className="eyebrow h-9 px-4 text-beam-text-soft">Kind</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[120px] px-4 text-right text-beam-text-soft">Days allowed</TableHead>
                <TableHead scope="col" className="eyebrow h-9 w-[110px] px-4 text-right text-beam-text-soft">Open cases</TableHead>
                <TableHead scope="col" className="h-9 w-[150px] px-4"><span className="sr-only">Open</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-id={r.id} className="h-[46px] border-beam-line hover:bg-beam-cream-deep">
                  <TableCell className="px-4 py-0 font-medium">{r.name}</TableCell>
                  <TableCell className="px-4 py-0">{REFERRER_KIND_WORD[r.kind]}</TableCell>
                  <TableCell className="tabular px-4 py-0 text-right">{r.slaDays}</TableCell>
                  <TableCell className="tabular px-4 py-0 text-right" data-slot="open-count">{open(r) ?? ''}</TableCell>
                  <TableCell className="px-4 py-0 text-right"><Link to={pipelineHref(r)} className={buttonVariants({ variant: 'outline', size: 'default' })} aria-label={`Open ${r.name} in the pipeline`}>Open in pipeline</Link></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-[13px] text-beam-text-muted">Days allowed is the referrer's own window for the whole report, informational until Beam supplies its service levels. Open cases are those not yet closed or cancelled.</p>
    </div>
  );
}
